import { useEffect, useMemo, useState } from "react";
import vppCatalogService from "../services/vppCatalogService";
import { BUILTIN_IOS_APPS, IOS_APP_BUNDLE_ALIASES, canonicalIosAppBundleId } from "../components/profileEditor/builtinIosApps";

function artworkFrom(...sources) {
  for (const item of sources) {
    const url = item?.artworkUrlSmall || item?.artworkUrl;
    if (url) return url;
  }
  return "";
}

/**
 * Vereinigt VPP-Katalog, eingebaute System-Apps und persistent gecachte Store-Icons.
 */
export function useProfileEditorAppCatalog(extraBundleIds = []) {
  const [vppApps, setVppApps] = useState([]);
  const [iconByBundle, setIconByBundle] = useState({});

  const bundleIds = useMemo(() => {
    const ids = new Set(BUILTIN_IOS_APPS.map((app) => app.bundleId));
    for (const id of extraBundleIds || []) {
      if (id) ids.add(canonicalIosAppBundleId(id));
    }
    return [...ids];
  }, [extraBundleIds]);

  useEffect(() => {
    vppCatalogService.getAssets({ productType: "App" })
      .then((data) => setVppApps((data.assets || []).filter((app) => app.bundleId)))
      .catch(() => setVppApps([]));
  }, []);

  useEffect(() => {
    if (!bundleIds.length) return;
    let cancelled = false;
    vppCatalogService.getAppIcons(bundleIds)
      .then((data) => {
        if (!cancelled) setIconByBundle(data.icons || {});
      })
      .catch(() => {
        if (!cancelled) setIconByBundle({});
      });
    return () => {
      cancelled = true;
    };
  }, [bundleIds]);

  const vppByBundle = useMemo(
    () => new Map(vppApps.map((app) => [app.bundleId, app])),
    [vppApps]
  );

  const appsById = useMemo(() => {
    const map = new Map();
    for (const app of BUILTIN_IOS_APPS) {
      const vpp = vppByBundle.get(app.bundleId);
      const cached = iconByBundle[app.bundleId];
      map.set(app.bundleId, {
        name: app.name,
        artworkUrl: artworkFrom(vpp, cached),
      });
    }
    for (const [alias, canonical] of Object.entries(IOS_APP_BUNDLE_ALIASES)) {
      if (map.has(canonical) && !map.has(alias)) {
        map.set(alias, map.get(canonical));
      }
    }
    for (const app of vppApps) {
      if (!map.has(app.bundleId)) {
        const cached = iconByBundle[app.bundleId];
        map.set(app.bundleId, {
          name: app.name || app.bundleId,
          artworkUrl: artworkFrom(app, cached),
        });
      }
    }
    for (const [bundleId, cached] of Object.entries(iconByBundle)) {
      if (!map.has(bundleId) && cached) {
        map.set(bundleId, {
          name: cached.name || bundleId,
          artworkUrl: artworkFrom(cached),
        });
      }
    }
    return map;
  }, [vppApps, vppByBundle, iconByBundle]);

  return { appsById, vppApps, vppByBundle, iconByBundle };
}
